import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environment/environment';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorHandlerService } from '@helpers/error-handler.service';
import { ImportDecisionsVM, SeriesDecision } from '@models/backend-generated-models/models';

// Response shapes from api/import. The API returns IActionResult, so NSwag doesn't generate these.

export interface ImportFinding {
  level: 'WARN' | 'ERROR';
  check: string;
  series: string;
  message: string;
  examples: string[];
}

export interface ImportMatch {
  siteId: number;
  siteName: string;
  logicalGroupId: number;
  logicalGroupName: string;
}

export interface ImportSiteSuggestion {
  siteId: number;
  name: string;
  why: string;
}

/** One line of the plan. Kind says what it does to Carbon: create (new), use (already there), overwrite (replaces stored readings), warn, note. */
export interface PlanAction {
  kind: 'create' | 'use' | 'overwrite' | 'warn' | 'note';
  text: string;
}

export type SeriesState = 'Matched' | 'NeedsMatch' | 'Ready' | 'Refused' | 'Skipped';

export interface ImportSeriesPlan {
  seriesId: string;
  sourceSite: string;
  sourceMeter: string;
  utility: string;
  measure: string;
  unit: string;
  readingCount: number;
  estimatedCount: number;
  /** Listed in an asset list but no readings in this upload: applying sets it up only. */
  layoutOnly: boolean;
  assetStatus?: string;
  assetType?: string;
  intervalMinutes: number;
  first: string;
  last: string;
  total: number;
  defaultInclude: boolean;
  state: SeriesState;
  reason?: string;
  match?: ImportMatch;
  actions: PlanAction[];
  siteSuggestions: ImportSiteSuggestion[];
  suggestedGroupName: string;
  suggestedCategoryId?: number;
  /** Its place on the site's combined sub-meter chart: the total the Remainder comes from, stacked, or left out because its parent counts it. */
  stack?: 'total' | 'stacked' | 'in-parent';
  stackParent?: string;
}

export interface ImportPreview {
  runId: number;
  fileName: string;
  format: string;
  sourceSystem: string;
  readingCount: number;
  firstInterval?: string;
  lastInterval?: string;
  validationStatus: 'PASS' | 'WARN' | 'FAIL';
  findings: ImportFinding[];
  series: ImportSeriesPlan[];
  canApply: boolean;
  anyNewSite: boolean;
  runStatus: string;
  sameFileBefore?: { runId: number; status: string; createdUtc: string; createdBy: string };
  /** Each file, when several were uploaded together. */
  files: { name: string; format: string; readings: number; meters: string[] }[];
  notes: string[];
}

export interface ImportSeriesResult {
  seriesId: string;
  label: string;
  status: string;
  siteName?: string;
  logicalGroupName?: string;
  rawRowsInserted: number;
  rawRowsReplaced: number;
  hoursWritten?: number;
  steps: string[];
  detail?: string;
}

export interface ImportResult {
  runId: number;
  status: string;
  series: ImportSeriesResult[];
  messages: string[];
}

export interface ImportRunListItem {
  runId: number;
  fileName: string;
  format: string;
  status: string;
  validationStatus: string;
  readingCount: number;
  firstInterval?: string;
  lastInterval?: string;
  createdUtc: string;
  createdBy: string;
  appliedUtc?: string;
  canUndo: boolean;
}

export interface ImportLookup {
  id: number;
  name: string;
  /** For a portal category: the report type behind it. For Carbon staff only. */
  detail?: string;
  /** For a portal category: suits an imported meter (the Consumption report). */
  recommended?: boolean;
}

export interface ImportLookups {
  sites: ImportLookup[];
  portals: ImportLookup[];
  categories: ImportLookup[];
}

export interface ImportGroupOption {
  logicalGroupId: number;
  name: string;
  readingTypeId?: number;
  readingTypeName?: string;
  mappedSeriesCount: number;
}

export { ImportDecisionsVM, SeriesDecision };

@Injectable({ providedIn: 'root' })
export class ImportService {
  private readonly base = `${environment.apiUrl}Import`;

  constructor(private http: HttpClient, private errorHandler: ErrorHandlerService) {
  }

  /** One file, or several (a whole store's exports), checked and planned as one upload. */
  preview$(files: File[]): Observable<ImportPreview> {
    const form = new FormData();
    for (const f of files) form.append('files', f, f.name);
    // Let the browser set the multipart Content-Type, boundary included.
    return this.http.post<ImportPreview>(`${this.base}/preview`, form).pipe(catchError(e => this.fail(e)));
  }

  plan$(runId: number, decisions: ImportDecisionsVM): Observable<ImportPreview> {
    return this.http.post<ImportPreview>(`${this.base}/${runId}/plan`, decisions).pipe(catchError(e => this.fail(e)));
  }

  apply$(runId: number, decisions: ImportDecisionsVM): Observable<ImportResult> {
    return this.http.post<ImportResult>(`${this.base}/${runId}/apply`, decisions).pipe(catchError(e => this.fail(e)));
  }

  undo$(runId: number): Observable<ImportResult> {
    return this.http.post<ImportResult>(`${this.base}/${runId}/undo`, {}).pipe(catchError(e => this.fail(e)));
  }

  runs$(top = 25): Observable<ImportRunListItem[]> {
    return this.http.get<ImportRunListItem[]>(`${this.base}/runs`, { params: { top: String(top) } }).pipe(catchError(e => this.fail(e)));
  }

  lookups$(): Observable<ImportLookups> {
    return this.http.get<ImportLookups>(`${this.base}/lookups`).pipe(catchError(e => this.fail(e)));
  }

  groups$(siteId: number): Observable<ImportGroupOption[]> {
    return this.http.get<ImportGroupOption[]>(`${this.base}/sites/${siteId}/groups`).pipe(catchError(e => this.fail(e)));
  }

  private fail(error: HttpErrorResponse) {
    this.errorHandler.handleError(error);
    return throwError(error);
  }
}
